import { useEffect, useRef, useState } from 'react'
import type { Node } from '@xyflow/react'

/**
 * The selection's filter (Huanrui, 2026-10-23)
 * -------------------------------------------------------------
 * Several kinds of thing selected at once -- frames, pictures, words, cards --
 * and the board shows one bar over the selection instead of every thing's
 * own tools: "Filter, 55 objects". Opened, it lists the kinds with how many
 * of each; choosing one keeps only those selected. As in Miro, a selected
 * frame's pictures and words count with it, so "Pictures" takes every
 * picture, those in the frames included, and lets the frames go. Only the
 * filter: nothing else on the bar.
 */

export type SelectKind = 'frame' | 'picture' | 'text' | 'card'

/** Which kind a thing on the board is, for the filter. Cards are not told apart. */
export function selectKind(node: Node): SelectKind {
  if (node.type === 'frame') return 'frame'
  if (node.type === 'image') return 'picture'
  if (node.type === 'label' || node.type === 'note') return 'text'
  return 'card'
}

const KINDS: { kind: SelectKind; label: string }[] = [
  { kind: 'frame', label: 'Frames' },
  { kind: 'picture', label: 'Pictures' },
  { kind: 'text', label: 'Text' },
  { kind: 'card', label: 'Cards' },
]

function KindIcon({ kind }: { kind: SelectKind }) {
  const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const
  return (
    <svg className="cselect__icon" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
      {kind === 'frame' ? (
        <path {...line} d="M6 2.5v15M14 2.5v15M2.5 6h15M2.5 14h15" />
      ) : kind === 'picture' ? (
        <>
          <rect {...line} x="2.5" y="3.5" width="15" height="13" rx="2.5" />
          <circle {...line} cx="7.2" cy="8" r="1.5" />
          <path {...line} d="M3 15l4.5-4.5 3 3 2.5-2.5 4.5 4.5" />
        </>
      ) : kind === 'text' ? (
        <path {...line} d="M4 5V3.5h12V5M10 3.5v13M7.5 16.5h5" />
      ) : (
        <>
          <rect {...line} x="2.5" y="4" width="15" height="12" rx="2.5" />
          <path {...line} d="M2.5 8h15" />
        </>
      )}
    </svg>
  )
}

export function SelectionFilter({
  counts,
  onPick,
}: {
  counts: Record<SelectKind, number>
  onPick: (kind: SelectKind) => void
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  // Closed by a press anywhere else, or Escape.
  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Element)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('keydown', escape)
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('keydown', escape)
    }
  }, [open])

  const total = KINDS.reduce((sum, one) => sum + counts[one.kind], 0)
  return (
    // Its own presses stay its own: not a press on the board, which would let the selection go.
    <div ref={root} className="cselect nodrag nopan" onPointerDown={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="cselect__button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((now) => !now)}
      >
        <span className="cselect__text">
          <span className="cselect__small">Filter</span>
          <span className="cselect__count">
            {total} object{total === 1 ? '' : 's'}
          </span>
        </span>
        <svg className="cselect__chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className="cselect__menu" role="menu">
          {KINDS.filter((one) => counts[one.kind] > 0).map((one) => (
            <button
              type="button"
              role="menuitem"
              key={one.kind}
              className="cselect__item"
              onClick={() => {
                setOpen(false)
                onPick(one.kind)
              }}
            >
              <KindIcon kind={one.kind} />
              <span className="cselect__label">{one.label}</span>
              <span className="cselect__n">{counts[one.kind]}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
